// @ts-nocheck
// mock api (disabled — using real backend)
// import './_apis_';

// i18n
import './locales/i18n';

// highlight
import './utils/highlight';

// scroll bar
import 'simplebar/src/simplebar.css';

// map
import 'azure-maps-control/dist/atlas.min.css';

// lightbox
import 'react-image-lightbox/style.css';

// editor
import 'react-quill/dist/quill.snow.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

// slick-carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// material
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// contexts
import { SettingsProvider } from './contexts/SettingsContext';
import { CollapseDrawerProvider } from './contexts/CollapseDrawerContext';
// components
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';

import { AuthProvider } from './contexts/JWTContext';

//
import App from './App';
import OneSignal from 'react-onesignal';
import { setupPwaInstallCapture } from './utils/pwaInstall';
import { setupServiceWorker } from './utils/registerSW';
import reportWebVitals from './reportWebVitals';

// ----------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Listen for Android Chrome's `beforeinstallprompt` before React boots —
// the event fires once and is lost if we miss it. F-4 will pop the install
// dialog after the first successful sign-on.
setupPwaInstallCapture();

// Initialise OneSignal push-notification SDK (no-ops when env var is unset)
if (import.meta.env.VITE_ONESIGNAL_APP_ID) {
  OneSignal.init({ appId: import.meta.env.VITE_ONESIGNAL_APP_ID }).catch(() => {});
}

async function bootMockApi() {
  if (import.meta.env.DEV && import.meta.env.VITE_USE_MOCK_API === 'true') {
    const { startMockApi } = await import('./mocks/browser');
    await startMockApi();
  }
}

const container = document.getElementById('root');
if (!container) throw new Error('Root container #root not found');
const root = createRoot(container);

bootMockApi().finally(() => {
  root.render(
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SettingsProvider>
              <CollapseDrawerProvider>
                <BrowserRouter>
                  <AuthProvider>
                    <App />
                  </AuthProvider>
                </BrowserRouter>
              </CollapseDrawerProvider>
            </SettingsProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
});

// Register the Workbox service worker and start polling for new builds so
// installed PWAs pick up deploys even when Android warm-resumes them without a
// navigation. See src/utils/registerSW.ts.
setupServiceWorker();

reportWebVitals();
if (import.meta.hot) {
  import.meta.hot.accept();
}